// =============================================================================
// hooks/simulator/useSimulate.ts — Mutation de simulação de crédito (F2-S06).
//
// POST /api/simulations → SimulationResult com tabela de amortização.
// TanStack Query useMutation — sem useEffect+fetch.
// Não invalida cache: simulação é append-only, não afeta listas.
//
// CONTRATO (F2-S11):
//   Request : body camelCase (leadId/productId/amount/termMonths)
//   Response: snake_case; monetários retornam como string → normalizados aqui
// =============================================================================

import { useMutation } from '@tanstack/react-query';

import { ApiError } from '../../lib/api';
import { api } from '../../lib/api';

import type { SimulationBody, SimulationResult } from './types';

// ─── Erro tipado de simulação ─────────────────────────────────────────────────

export type SimulationErrorCode =
  | 'VALIDATION_ERROR' // 422 — fora dos limites da regra
  | 'NO_RULE_FOR_CITY' // 409 — sem regra ativa para a cidade do lead
  | 'FLAG_DISABLED' // 503 / 403 — módulo desativado
  | 'FORBIDDEN' // 403 — sem permissão
  | 'FIRST_DUE_DATE_TOO_SOON' // 422 — 1ª parcela antes da carência mínima (30 dias)
  | 'FIRST_DUE_DATE_TOO_LATE' // 422 — 1ª parcela além do teto de carência (90 dias)
  | 'UNKNOWN';

export interface SimulationError {
  code: SimulationErrorCode;
  message: string;
  fieldErrors?: Record<string, string>; // campos com erros para 422
  /**
   * Campo do formulário a que o erro se refere — quando presente, a UI deve
   * exibir a mensagem junto do campo (não em toast/banner genérico). Ver
   * F44-S06: "erro do backend aparece no campo".
   */
  field?: 'firstDueDate';
}

/**
 * Códigos estáveis que `FirstDueDateTooSoonError`/`FirstDueDateTooLateError`
 * anexam em `details.code` (ver `apps/api/src/modules/simulations/service.ts`).
 */
const FIRST_DUE_DATE_DETAIL_CODES = new Set<SimulationErrorCode>([
  'FIRST_DUE_DATE_TOO_SOON',
  'FIRST_DUE_DATE_TOO_LATE',
]);

/**
 * `details` é corpo de resposta de rede (ver `ApiError.details` em `lib/api.ts`)
 * — valida o formato antes de confiar em `.code`.
 */
function readFirstDueDateDetailCode(details: unknown): SimulationErrorCode | null {
  if (details === null || typeof details !== 'object') return null;
  const code = (details as { code?: unknown }).code;
  if (typeof code === 'string' && FIRST_DUE_DATE_DETAIL_CODES.has(code as SimulationErrorCode)) {
    return code as SimulationErrorCode;
  }
  return null;
}

/**
 * FALLBACK — casa o TEXTO da mensagem quando o erro não trouxe `details.code`
 * (respostas antigas, ou rotas que ainda não anexam `details`). Frágil a
 * qualquer ajuste de redação no backend: usar `readFirstDueDateDetailCode`
 * sempre que possível.
 */
function classifyFirstDueDateMessage(message: string): SimulationErrorCode | null {
  if (message.includes('não pode vencer antes de')) return 'FIRST_DUE_DATE_TOO_SOON';
  if (message.includes('não pode vencer depois de')) return 'FIRST_DUE_DATE_TOO_LATE';
  return null;
}

/**
 * Exportado apenas para teste direto (evita mirror de lógica que diverge do
 * real — ver F45-S07). Não é API pública do hook.
 */
export function classifyError(err: unknown): SimulationError {
  if (err instanceof ApiError) {
    if (err.status === 422) {
      const dateCode =
        readFirstDueDateDetailCode(err.details) ?? classifyFirstDueDateMessage(err.message);
      if (dateCode) {
        return {
          code: dateCode,
          message: err.message,
          field: 'firstDueDate',
        };
      }
      return {
        code: 'VALIDATION_ERROR',
        message: err.message,
      };
    }
    if (err.status === 409) {
      return {
        code: 'NO_RULE_FOR_CITY',
        message: err.message || 'Sem regra de crédito ativa para a cidade do lead.',
      };
    }
    if (err.status === 503) {
      return {
        code: 'FLAG_DISABLED',
        message: 'Módulo de simulação desativado.',
      };
    }
    if (err.status === 403) {
      return {
        code: 'FORBIDDEN',
        message: 'Sem permissão para simular crédito.',
      };
    }
  }
  return {
    code: 'UNKNOWN',
    message: err instanceof Error ? err.message : 'Erro desconhecido.',
  };
}

// ─── Shape bruto do backend (antes da normalização) ──────────────────────────

/**
 * Response bruta do backend — monetários são string ("5000.00").
 * Apenas para uso interno do normalizer.
 */
interface RawSimulationResponse {
  id: string;
  organization_id: string;
  lead_id: string;
  product_id: string;
  rule_version_id: string;
  amount_requested: string; // string no wire — ex: "5000.00"
  term_months: number;
  monthly_payment: string; // string no wire
  total_amount: string; // string no wire
  total_interest: string; // string no wire
  rate_monthly_snapshot: string; // string no wire — ex: "0.0199"
  amortization_method: 'price' | 'sac';
  amortization_table: Array<{
    number: number;
    payment: number;
    principal: number;
    interest: number;
    balance: number;
    /** Presente apenas quando a simulação tem first_due_date (F44-S06). */
    due_date?: string;
  }>;
  /** Data acordada da 1ª parcela. Null em simulações anteriores à F44. */
  first_due_date: string | null;
  /** Dias de carência até o 1º vencimento. Null em simulações anteriores à F44. */
  grace_days: number | null;
  /** Juros da carência (string no wire — F44-S06). Null em simulações anteriores à F44. */
  grace_interest: string | null;
  origin: 'manual' | 'ai' | 'import';
  created_by_user_id: string | null;
  created_at: string;
}

/**
 * Normaliza a resposta bruta do backend:
 * converte os campos monetários string→number uma única vez.
 * A UI trabalha sempre com number.
 */
function normalizeResponse(raw: RawSimulationResponse): SimulationResult {
  return {
    id: raw.id,
    organization_id: raw.organization_id,
    lead_id: raw.lead_id,
    product_id: raw.product_id,
    rule_version_id: raw.rule_version_id,
    amount_requested: Number(raw.amount_requested),
    term_months: raw.term_months,
    monthly_payment: Number(raw.monthly_payment),
    total_amount: Number(raw.total_amount),
    total_interest: Number(raw.total_interest),
    rate_monthly_snapshot: Number(raw.rate_monthly_snapshot),
    amortization_method: raw.amortization_method,
    amortization_table: raw.amortization_table,
    first_due_date: raw.first_due_date,
    grace_days: raw.grace_days,
    grace_interest: raw.grace_interest !== null ? Number(raw.grace_interest) : null,
    origin: raw.origin,
    created_by_user_id: raw.created_by_user_id,
    created_at: raw.created_at,
  };
}

// ─── Fetcher ─────────────────────────────────────────────────────────────────

async function postSimulation(body: SimulationBody): Promise<SimulationResult> {
  const raw = await api.post<RawSimulationResponse>('/api/simulations', body);
  return normalizeResponse(raw);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Mutation de simulação de crédito.
 *
 * Uso:
 *   const { mutate, isPending, data, simulationError, reset } = useSimulate();
 *   mutate({ leadId, productId, amount, termMonths });
 *
 * Erros classificados em SimulationErrorCode para UX específica por código.
 */
export function useSimulate(): {
  mutate: (body: SimulationBody) => void;
  isPending: boolean;
  data: SimulationResult | undefined;
  simulationError: SimulationError | null;
  reset: () => void;
} {
  const { mutate, isPending, data, error, reset } = useMutation<
    SimulationResult,
    unknown,
    SimulationBody
  >({
    mutationFn: postSimulation,
  });

  const simulationError: SimulationError | null = error ? classifyError(error) : null;

  return { mutate, isPending, data, simulationError, reset };
}
